import type { JSX } from "react";

import classNames from "classnames";
import styled, { css } from "styled-components";

import { rgba } from "@similarweb/styles";

export type Variant = "primary" | "secondary";

interface ProgressBarProps {
    width: number;
    height?: number;
    className?: string;
    backgroundColor?: string;
    isCompare?: boolean;
    innerColor?: string;
    variant?: Variant;
}

const ProgressBarWrapper = styled.div<{ $height: number; $backgroundColor: string }>`
    ${({ $height, $backgroundColor }) => css`
        height: ${$height}px;
        background-color: ${$backgroundColor};
    `}
`;

const ProgressBarInner = styled.div<{ $width: number; $color: string; $variant: Variant }>`
    ${({ $width, $color, $variant }) => {
        // Add 0.5px in case that width > 0 so that width would not floor to 0 in small columns
        const width = $width ? `calc(${$width}% + 0.5px)` : 0;

        let background = $color;
        if ($variant === "secondary") {
            const altColor = rgba($color, 0.3);
            background = `repeating-linear-gradient(135deg, ${altColor}, ${altColor} 2px, ${$color} 2px, ${$color} 4px)`;
        }

        return css`
            .sw-progress-bar & {
                width: ${width};
                background: ${background};
            }
        `;
    }}
`;

export const ProgressBar = ({
    isCompare,
    className,
    width,
    height,
    backgroundColor,
    innerColor,
    variant = "primary",
}: ProgressBarProps): JSX.Element => {
    return (
        <ProgressBarWrapper
            className={classNames("sw-progress-bar", { "sw-progress-bar--compare": isCompare }, className)}
            $height={height}
            $backgroundColor={backgroundColor}
        >
            <ProgressBarInner
                className={classNames("sw-progress-bar-inner", { "very-long": width >= 98.3 })}
                $width={width}
                $color={innerColor}
                $variant={variant}
            />
        </ProgressBarWrapper>
    );
};
